<template>
  <div class="page">
    <div class="page-header">
      <div>
        <h1 class="page-title">Saques</h1>
        <p class="page-help" v-if="isOrganizador">Solicite saque do saldo dos seus eventos.</p>
        <p class="page-help" v-else>Solicitações dos organizadores. Marque como enviado quando fizer o PIX.</p>
      </div>
      <button v-if="isOrganizador && eventosDisponiveis.length" class="btn-primary" @click="openSolicitar()">+ Solicitar saque</button>
    </div>

    <div class="tabs card">
      <button class="tab" :class="{ active: filtro === '' }" @click="filtro=''; fetch()">Todos ({{ counts.total }})</button>
      <button class="tab" :class="{ active: filtro === 'PENDENTE' }" @click="filtro='PENDENTE'; fetch()">
        Pendentes
        <span v-if="counts.pendente" class="badge">{{ counts.pendente }}</span>
      </button>
      <button class="tab" :class="{ active: filtro === 'ENVIADO' }" @click="filtro='ENVIADO'; fetch()">Enviados</button>
      <button class="tab" :class="{ active: filtro === 'REJEITADO' }" @click="filtro='REJEITADO'; fetch()">Rejeitados</button>
    </div>

    <SkeletonLoader v-if="loading" />
    <EmptyState v-else-if="!saques.length" icon="💸" title="Sem saques" description="Nenhuma solicitação por aqui ainda." />

    <div v-else class="lista">
      <div v-for="s in saques" :key="s.id" class="saque-card card" @click="openDetail(s)">
        <div class="saque-topo">
          <span class="chip" :class="`chip-${s.status.toLowerCase()}`">{{ statusLabel(s.status) }}</span>
          <span class="saque-data">{{ formatDate(s.solicitadoEm) }}</span>
        </div>
        <div class="saque-corpo">
          <div class="saque-info">
            <strong class="saque-nome">{{ s.evento.nome }}</strong>
            <span class="saque-org" v-if="!isOrganizador">{{ s.solicitadoPor.name }}</span>
            <span class="saque-chave">{{ s.tipoChavePix }}: {{ s.chavePix }}</span>
          </div>
          <div class="saque-valor">
            <strong>{{ formatMoney(s.valor) }}</strong>
          </div>
        </div>
      </div>
    </div>

    <!-- Solicitar saque (organizador) -->
    <div v-if="solicitando" class="modal-overlay" @click.self="solicitando = false">
      <div class="modal-card">
        <h2 class="modal-title">Solicitar saque</h2>
        <div class="form-row">
          <label class="label">Evento</label>
          <select class="select" v-model="novo.eventoId" @change="carregarSaldo()">
            <option value="">Escolha</option>
            <option v-for="e in eventosDisponiveis" :key="e.id" :value="e.id">{{ e.nome }}</option>
          </select>
        </div>

        <div v-if="saldoAtual" class="saldo-info">
          <div class="linha"><span>Saldo disponível</span><strong>{{ formatMoney(saldoAtual.disponivel) }}</strong></div>
          <div class="linha muted"><small>Recebido: {{ formatMoney(saldoAtual.totalRecebido) }} · Repassado/sacado: {{ formatMoney(saldoAtual.totalRepassado + saldoAtual.saquesEnviados) }} · Pendente: {{ formatMoney(saldoAtual.saquesPendentes) }}</small></div>
        </div>

        <div class="form-row">
          <label class="label">Valor a sacar</label>
          <input class="input" type="number" min="0.01" step="0.01" :max="saldoAtual?.disponivel || 0" v-model.number="novo.valor" />
        </div>

        <div class="form-grid">
          <div class="form-row">
            <label class="label">Tipo de chave PIX</label>
            <select class="select" v-model="novo.tipoChavePix">
              <option value="CPF">CPF</option>
              <option value="CNPJ">CNPJ</option>
              <option value="EMAIL">Email</option>
              <option value="TELEFONE">Telefone</option>
              <option value="ALEATORIA">Chave aleatória</option>
            </select>
          </div>
          <div class="form-row">
            <label class="label">Chave</label>
            <input class="input" v-model="novo.chavePix" maxlength="120" />
          </div>
        </div>

        <div class="form-row">
          <label class="label">Observação (opcional)</label>
          <textarea class="textarea" rows="2" v-model="novo.observacaoOrg" maxlength="500"></textarea>
        </div>

        <div v-if="erro" class="erro">{{ erro }}</div>

        <div class="modal-actions">
          <button class="btn-secondary" @click="solicitando = false">Cancelar</button>
          <button class="btn-primary" :disabled="salvando || !podeSalvar" @click="salvar">
            {{ salvando ? 'Enviando...' : 'Solicitar saque' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Detalhes -->
    <div v-if="detail" class="modal-overlay" @click.self="detail = null">
      <div class="modal-card">
        <h2 class="modal-title">Solicitação de saque</h2>
        <div class="detail-row"><span>Status</span><span class="chip" :class="`chip-${detail.status.toLowerCase()}`">{{ statusLabel(detail.status) }}</span></div>
        <div class="detail-row"><span>Evento</span><strong>{{ detail.evento.nome }}</strong></div>
        <div class="detail-row"><span>Organizador</span><span>{{ detail.solicitadoPor.name }} ({{ detail.solicitadoPor.email }})</span></div>
        <div class="detail-row"><span>Valor</span><strong>{{ formatMoney(detail.valor) }}</strong></div>
        <div class="detail-row"><span>Chave PIX</span>
          <div class="pix-copy">
            <code>{{ detail.tipoChavePix }}: {{ detail.chavePix }}</code>
            <button type="button" class="btn-copy" @click="copiar(detail.chavePix)">Copiar</button>
          </div>
        </div>
        <div v-if="detail.observacaoOrg" class="detail-row"><span>Observação</span><span>{{ detail.observacaoOrg }}</span></div>
        <div class="detail-row"><span>Solicitado em</span><span>{{ formatDate(detail.solicitadoEm) }}</span></div>
        <div v-if="detail.processadoEm" class="detail-row"><span>{{ detail.status === 'ENVIADO' ? 'Enviado em' : 'Processado em' }}</span><span>{{ formatDate(detail.processadoEm) }} por {{ detail.processadoPor?.name }}</span></div>
        <div v-if="detail.motivoRejeicao" class="detail-row danger-row"><span>Motivo rejeição</span><span>{{ detail.motivoRejeicao }}</span></div>

        <div class="modal-actions" v-if="detail.status === 'PENDENTE'">
          <button
            v-if="podeAdminMarcar"
            class="btn-secondary btn-danger"
            :disabled="processando"
            @click="rejeitar"
          >Rejeitar</button>
          <button
            v-if="podeAdminMarcar"
            class="btn-primary"
            :disabled="processando"
            @click="marcarEnviado"
          >{{ processando ? '...' : 'Marcar como enviado' }}</button>
          <button
            v-else-if="isOrganizador && detail.solicitadoPor.id === authStore.adminId"
            class="btn-secondary btn-danger"
            :disabled="processando"
            @click="cancelarProprio"
          >Cancelar solicitação</button>
          <button class="btn-secondary" @click="detail = null">Fechar</button>
        </div>
        <div v-else class="modal-actions">
          <button class="btn-primary" @click="detail = null">Fechar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { inject } from 'vue'
import api from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { useEventosStore } from '../../stores/eventos'
import SkeletonLoader from '../../components/ui/SkeletonLoader.vue'
import EmptyState from '../../components/ui/EmptyState.vue'

export default {
  name: 'SaquesPage',
  components: { SkeletonLoader, EmptyState },
  setup() {
    return { pusher: inject('pusher') }
  },
  data() {
    return {
      saques: [],
      loading: true,
      filtro: '',
      solicitando: false,
      detail: null,
      salvando: false,
      processando: false,
      erro: '',
      saldoAtual: null,
      novo: { eventoId: '', valor: 0, chavePix: '', tipoChavePix: 'CPF', observacaoOrg: '' },
      channel: null
    }
  },
  computed: {
    authStore() { return useAuthStore() },
    eventosStore() { return useEventosStore() },
    isOrganizador() { return this.authStore.role === 'ORGANIZADOR' },
    podeAdminMarcar() { return this.authStore.role === 'ROOT' || this.authStore.role === 'ADMIN' },
    eventosDisponiveis() {
      const eventos = this.eventosStore.eventos || []
      if (this.isOrganizador) {
        return eventos.filter(e => e.status !== 'ARQUIVADO')
      }
      return eventos
    },
    counts() {
      const total = this.saques.length
      const pendente = this.saques.filter(s => s.status === 'PENDENTE').length
      return { total, pendente }
    },
    podeSalvar() {
      return this.novo.eventoId && this.novo.valor > 0 && this.novo.chavePix.trim().length >= 4
    }
  },
  async mounted() {
    await this.eventosStore.fetchAll()
    await this.fetch()
    this.channel = this.pusher.subscribe('saques')
    this.channel.bind('saque-solicitado', () => {
      if (this.podeAdminMarcar) {
        this.$toast.open({ message: 'Novo saque solicitado', type: 'info', position: 'top-right' })
        this.fetch()
      }
    })
    this.channel.bind('saque-atualizado', () => this.fetch())
  },
  beforeUnmount() {
    if (this.channel) this.pusher.unsubscribe('saques')
  },
  methods: {
    async fetch() {
      this.loading = true
      try {
        const { data } = await api.get('/saques', { params: this.filtro ? { status: this.filtro } : {} })
        this.saques = data.saques
      } catch (err) {
        this.$toast.open({ message: err.response?.data?.message || 'Erro', type: 'error', position: 'top-right' })
      } finally {
        this.loading = false
      }
    },
    openSolicitar() {
      this.solicitando = true
      this.erro = ''
      this.saldoAtual = null
      this.novo = { eventoId: '', valor: 0, chavePix: '', tipoChavePix: 'CPF', observacaoOrg: '' }
      if (this.eventosDisponiveis.length === 1) {
        this.novo.eventoId = this.eventosDisponiveis[0].id
        this.carregarSaldo()
      }
    },
    async carregarSaldo() {
      this.saldoAtual = null
      if (!this.novo.eventoId) return
      try {
        const { data } = await api.get(`/saques/saldo/${this.novo.eventoId}`)
        this.saldoAtual = data
        if (this.novo.valor === 0 || this.novo.valor > data.disponivel) {
          this.novo.valor = data.disponivel
        }
      } catch (err) {
        this.saldoAtual = null
        this.erro = err.response?.data?.message || 'Falha ao consultar saldo'
      }
    },
    async salvar() {
      this.salvando = true
      this.erro = ''
      try {
        await api.post('/saques', this.novo)
        this.$toast.open({ message: 'Saque solicitado', type: 'success', position: 'top-right' })
        this.solicitando = false
        await this.fetch()
      } catch (err) {
        this.erro = err.response?.data?.message || 'Falha ao solicitar'
      } finally {
        this.salvando = false
      }
    },
    async openDetail(s) {
      this.detail = { ...s }
    },
    async marcarEnviado() {
      if (!confirm(`Confirma que já enviou R$ ${this.detail.valor.toFixed(2)} pra ${this.detail.solicitadoPor.name}?`)) return
      this.processando = true
      try {
        const { data } = await api.post(`/saques/${this.detail.id}/enviar`)
        this.detail = data.saque
        this.$toast.open({ message: 'Marcado como enviado', type: 'success', position: 'top-right' })
        await this.fetch()
      } catch (err) {
        this.$toast.open({ message: err.response?.data?.message || 'Erro', type: 'error', position: 'top-right' })
      } finally {
        this.processando = false
      }
    },
    async rejeitar() {
      const motivo = prompt('Motivo da rejeição:')
      if (!motivo || motivo.length < 3) return
      this.processando = true
      try {
        const { data } = await api.post(`/saques/${this.detail.id}/rejeitar`, { motivo })
        this.detail = data.saque
        this.$toast.open({ message: 'Rejeitado', type: 'warning', position: 'top-right' })
        await this.fetch()
      } catch (err) {
        this.$toast.open({ message: err.response?.data?.message || 'Erro', type: 'error', position: 'top-right' })
      } finally {
        this.processando = false
      }
    },
    async cancelarProprio() {
      if (!confirm('Cancelar essa solicitação?')) return
      this.processando = true
      try {
        const { data } = await api.post(`/saques/${this.detail.id}/cancelar`)
        this.detail = data.saque
        this.$toast.open({ message: 'Cancelado', type: 'success', position: 'top-right' })
        await this.fetch()
      } catch (err) {
        this.$toast.open({ message: err.response?.data?.message || 'Erro', type: 'error', position: 'top-right' })
      } finally {
        this.processando = false
      }
    },
    copiar(v) {
      navigator.clipboard.writeText(v)
      this.$toast.open({ message: 'Chave copiada', type: 'success', position: 'top-right', duration: 1500 })
    },
    statusLabel(s) {
      return { PENDENTE: 'Pendente', ENVIADO: 'Enviado', REJEITADO: 'Rejeitado', CANCELADO: 'Cancelado' }[s] || s
    },
    formatDate(v) {
      try { return new Date(v).toLocaleString('pt-BR') } catch { return v }
    },
    formatMoney(v) {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
    }
  }
}
</script>

<style scoped>
.page { max-width: 900px; margin: 0 auto; padding: var(--spacing-base); display: flex; flex-direction: column; gap: var(--spacing-base); }
.page-header { display: flex; justify-content: space-between; align-items: flex-start; gap: var(--spacing-base); flex-wrap: wrap; }
.page-title { margin: 0 0 4px; }
.page-help { color: var(--color-text-muted); margin: 0; font-size: var(--text-sm); }
.tabs { display: flex; gap: 4px; padding: 6px; overflow-x: auto; }
.tab {
  background: none; border: 0; padding: 8px 14px; border-radius: 4px;
  cursor: pointer; font-size: 13px; color: var(--color-text-muted);
  display: flex; align-items: center; gap: 6px; white-space: nowrap;
}
.tab.active { background: var(--color-accent); color: white; }
.badge { background: var(--color-danger); color: white; font-size: 11px; padding: 2px 6px; border-radius: 8px; }

.lista { display: flex; flex-direction: column; gap: 10px; }
.saque-card { padding: 14px 18px; cursor: pointer; transition: transform 0.15s ease, box-shadow 0.15s ease; }
.saque-card:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(0,0,0,0.08); }
.saque-topo { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
.saque-data { color: var(--color-text-muted); font-size: 12px; }
.saque-corpo { display: flex; justify-content: space-between; align-items: center; gap: var(--spacing-base); }
.saque-info { display: flex; flex-direction: column; gap: 2px; }
.saque-nome { font-size: 15px; }
.saque-org { color: var(--color-text-muted); font-size: 13px; }
.saque-chave { color: var(--color-text-muted); font-size: 12px; font-family: monospace; }
.saque-valor strong { font-size: 20px; color: var(--color-accent); }

.chip { display: inline-block; font-size: 11px; padding: 2px 8px; border-radius: 12px; font-weight: 600; text-transform: uppercase; }
.chip-pendente { background: #fef3c7; color: #92400e; }
.chip-enviado { background: #d1fae5; color: #065f46; }
.chip-rejeitado { background: #fee2e2; color: #991b1b; }
.chip-cancelado { background: #e5e7eb; color: #4b5563; }

.saldo-info { background: #f8fafc; padding: 12px; border-radius: 6px; margin-bottom: var(--spacing-md); border: 1px solid #e2e8f0; }
.saldo-info .linha { display: flex; justify-content: space-between; align-items: center; }
.saldo-info .muted { color: var(--color-text-muted); font-size: 12px; margin-top: 4px; }

.form-row { display: flex; flex-direction: column; gap: 4px; margin-bottom: 12px; }
.form-grid { display: grid; grid-template-columns: 1fr 2fr; gap: 12px; }
@media (max-width: 500px) { .form-grid { grid-template-columns: 1fr; } }
.textarea { border: 1px solid #ddd; border-radius: 4px; padding: 8px 10px; font-family: inherit; resize: vertical; }
.select { border: 1px solid #ddd; border-radius: 4px; padding: 8px 10px; }

.erro { background: #fee2e2; color: #b91c1c; padding: 8px 12px; border-radius: 4px; font-size: 13px; margin-bottom: 12px; }

.detail-row { display: flex; justify-content: space-between; align-items: center; padding: 6px 0; border-bottom: 1px dashed #eee; font-size: 14px; gap: var(--spacing-base); }
.detail-row span:first-child { color: var(--color-text-muted); font-size: 12px; text-transform: uppercase; letter-spacing: 0.3px; }
.detail-row.danger-row span { color: #991b1b; }
.pix-copy { display: flex; align-items: center; gap: 8px; }
.pix-copy code { background: #f1f5f9; padding: 4px 8px; border-radius: 3px; font-size: 13px; }
.btn-copy { background: var(--color-accent); color: white; border: 0; padding: 4px 10px; border-radius: 3px; cursor: pointer; font-size: 12px; }
.btn-danger { background: #fee2e2 !important; color: #b91c1c !important; border-color: #fca5a5 !important; }
</style>
